import { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateTrafficSavings } from "../actions";
import axios from "../helpers/axios";

const STORAGE_KEY = "localTrafficState";
const HIGH_QUALITY_PERSONAL_PLAN = "user_pro";
const HIGH_QUALITY_BYPASS_ROLES = new Set(["admin", "superAdmin"]);

const normalizeTrafficState = (value) => {
    return value !== false;
};

const isActivePersonalSubscription = (subscription = {}) => {
    if (!subscription?.validUntil) return false;
    if (subscription.plan !== HIGH_QUALITY_PERSONAL_PLAN) return false;
    if (["inactive", "past_due"].includes(subscription.status)) return false;

    const validUntil = new Date(subscription.validUntil);

    return !Number.isNaN(validUntil.getTime()) && validUntil.getTime() > Date.now();
};

const saveLocalTrafficState = (value) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeTrafficState(value)));
    } catch (_e) { }
};

const emptyAccess = {
    loaded: false,
    loading: false,
    allowed: false,
    source: "none",
    plan: "none",
    officeId: null,
    officeName: "",
    validUntil: null,
};

const useTrafficState = () => {
    const dispatch = useDispatch();
    const { user, authenticate } = useSelector((state) => state.auth || {});
    const [pending, setPending] = useState(false);
    const [serverAccess, setServerAccess] = useState(emptyAccess);

    const userTrafficState = normalizeTrafficState(user?.trafficSavings);
    const personalAccess = isActivePersonalSubscription(user?.subscription);
    const adminAccess = HIGH_QUALITY_BYPASS_ROLES.has(user?.role);

    useEffect(() => {
        let cancelled = false;

        if (!authenticate || !user?._id) {
            setServerAccess({
                ...emptyAccess,
                loaded: true,
            });
            return undefined;
        }

        if (adminAccess) {
            setServerAccess({
                ...emptyAccess,
                loaded: true,
                allowed: true,
                source: "admin",
                plan: "admin",
            });
            return undefined;
        }

        if (personalAccess) {
            setServerAccess({
                ...emptyAccess,
                loaded: true,
                allowed: true,
                source: "personal",
                plan: HIGH_QUALITY_PERSONAL_PLAN,
                validUntil: user?.subscription?.validUntil || null,
            });
            return undefined;
        }

        setServerAccess((current) => ({
            ...current,
            loading: true,
        }));

        axios.get("/user/image-quality-access")
            .then((response) => {
                if (cancelled) return;

                const data = response?.data || {};

                setServerAccess({
                    loaded: true,
                    loading: false,
                    allowed: data.allowed === true,
                    source: data.source || "none",
                    plan: data.plan || "none",
                    officeId: data.officeId || null,
                    officeName: data.officeName || "",
                    validUntil: data.validUntil || null,
                });
            })
            .catch(() => {
                if (cancelled) return;

                setServerAccess({
                    ...emptyAccess,
                    loaded: true,
                });
            });

        return () => {
            cancelled = true;
        };
    }, [
        adminAccess,
        authenticate,
        personalAccess,
        user?._id,
        user?.subscription?.validUntil,
    ]);

    const canUseHighQuality = !!(
        authenticate &&
        (
            adminAccess ||
            personalAccess ||
            serverAccess.allowed
        )
    );

    const accessChecking = !!(
        authenticate &&
        !adminAccess &&
        !personalAccess &&
        serverAccess.loading
    );

    const trafficState = useMemo(() => {
        if (accessChecking) return true;
        if (!canUseHighQuality) return true;

        return userTrafficState;
    }, [accessChecking, canUseHighQuality, userTrafficState]);

    const imageQuality = trafficState ? "low" : "high";

    useEffect(() => {
        saveLocalTrafficState(trafficState);
    }, [trafficState]);

    const setTrafficState = useCallback(async (nextValue) => {
        if (pending || accessChecking) return trafficState;

        const requestedValue = normalizeTrafficState(nextValue);
        const value = requestedValue === false && !canUseHighQuality
            ? true
            : requestedValue;

        if (!authenticate || value === trafficState) {
            saveLocalTrafficState(trafficState);
            return trafficState;
        }

        setPending(true);

        try {
            await dispatch(updateTrafficSavings({ trafficSavings: value }));
            return value;
        } finally {
            setPending(false);
        }
    }, [
        accessChecking,
        authenticate,
        canUseHighQuality,
        dispatch,
        pending,
        trafficState,
    ]);

    const toggleTrafficState = useCallback(() => {
        return setTrafficState(!trafficState);
    }, [setTrafficState, trafficState]);

    return [
        trafficState,
        toggleTrafficState,
        {
            setTrafficState,
            imageQuality,
            isLowQuality: imageQuality === "low",
            isHighQuality: imageQuality === "high",
            pending,
            accessChecking,
            canUseHighQuality,
            highQualityLocked: !canUseHighQuality,
            highQualitySubscriptionActive: personalAccess || serverAccess.source === "office",
            highQualityAccessSource: adminAccess
                ? "admin"
                : personalAccess
                    ? "personal"
                    : serverAccess.source,
            highQualityPlan: adminAccess
                ? "admin"
                : personalAccess
                    ? HIGH_QUALITY_PERSONAL_PLAN
                    : serverAccess.plan,
            highQualityOfficeId: serverAccess.officeId,
            highQualityOfficeName: serverAccess.officeName,
            highQualityValidUntil: personalAccess
                ? user?.subscription?.validUntil || null
                : serverAccess.validUntil,
        },
    ];
};

export default useTrafficState;
